import React, { useState } from 'react';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import BrewCalculator from './components/BrewCalculator';
import YieldCalculator from './components/YieldCalculator';
import ConversionCalculator from './components/ConversionCalculator';
import CostCalculator from './components/CostCalculator';
import HeroBanner from './components/layout/HeroBanner';
import Footer from './components/layout/Footer';

const { FiCoffee, FiBox, FiRefreshCw, FiDollarSign } = FiIcons;

function App() {
  const [activeTab, setActiveTab] = useState('brew');

  const tabs = [
    { id: 'brew', name: 'Brew Ratio', icon: FiCoffee, description: 'Oz per Pot' },
    { id: 'yield', name: 'Yield', icon: FiBox, description: 'Inventory to Pots' },
    { id: 'convert', name: 'Conversions', icon: FiRefreshCw, description: 'Weight to Volume' },
    { id: 'cost', name: 'Cost Analysis', icon: FiDollarSign, description: 'Price per Serving' },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans selection:bg-yellow-500/30">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-950/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://media-manager-c.questera.ai/greta-media/00f9785630fa510c7b7249771f188009b71ad762fbad38d2b89b3733d51394c2722890c07f10237a0b5d877487ecaead/images/aW1hZ2UvcG5n/b347cf89d959987cc22861640fd38b47.png" 
              alt="Bat Coffee Logo" 
              className="w-12 h-12 object-contain drop-shadow-[0_0_10px_rgba(234,179,8,0.2)]"
            />
            <div>
              <h1 className="text-xl font-black tracking-tighter text-white uppercase leading-none">Bat Coffee</h1>
              <p className="text-[10px] text-yellow-500 font-bold tracking-[0.2em] uppercase mt-1">Calculators</p>
            </div>
          </div>
          
          <a 
            href="https://batcoffee.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-xs font-bold text-slate-400 hover:text-white transition-colors flex items-center gap-2"
          >
            BATCOFFEE.COM
            <SafeIcon icon={FiIcons.FiExternalLink} />
          </a>
        </div>
      </header>

      <HeroBanner />

      <main className="max-w-5xl mx-auto px-4 py-12 flex flex-col lg:flex-row gap-12">
        {/* Sidebar Navigation */}
        <nav className="w-full lg:w-72 flex-shrink-0">
          <div className="sticky top-24 space-y-2">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest px-4 mb-4">Select Tool</p>
            <div className="flex lg:flex-col gap-2 overflow-x-auto pb-4 lg:pb-0 hide-scrollbar">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-4 px-4 py-4 rounded-xl transition-all whitespace-nowrap w-full text-left group ${
                    activeTab === tab.id
                      ? 'bg-yellow-500 text-slate-950 font-bold shadow-lg shadow-yellow-500/10'
                      : 'bg-slate-900 text-slate-400 hover:bg-slate-800 hover:text-slate-200 border border-slate-800/50'
                  }`}
                >
                  <div className={`p-2 rounded-lg transition-colors ${
                    activeTab === tab.id ? 'bg-slate-950/20' : 'bg-slate-950 group-hover:bg-slate-700'
                  }`}>
                    <SafeIcon icon={tab.icon} className="text-xl" />
                  </div>
                  <div>
                    <div className="text-sm leading-none mb-1">{tab.name}</div>
                    <div className={`text-[10px] font-medium opacity-60 ${
                      activeTab === tab.id ? 'text-slate-900' : 'text-slate-500'
                    }`}>
                      {tab.description}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </nav>

        {/* Content Area */}
        <div className="flex-1">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-10 shadow-2xl relative overflow-hidden">
            {/* Decorative element */}
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-yellow-500/5 rounded-full blur-3xl pointer-events-none" />
            
            <div className="relative z-10">
              {activeTab === 'brew' && <BrewCalculator />}
              {activeTab === 'yield' && <YieldCalculator />}
              {activeTab === 'convert' && <ConversionCalculator />}
              {activeTab === 'cost' && <CostCalculator />}
            </div>
          </div>

          {/* Quick Tip */}
          <div className="mt-8 p-6 bg-slate-900/40 border border-slate-800/50 rounded-2xl flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-yellow-500/10 flex-shrink-0 flex items-center justify-center text-yellow-500">
              <SafeIcon icon={FiIcons.FiZap} />
            </div>
            <div>
              <h4 className="text-sm font-bold text-white mb-1">Pro Tip: Freshness Matters</h4>
              <p className="text-xs text-slate-500 leading-relaxed">
                For the best results with Bat Coffee, always grind your beans immediately before brewing. 
                Whole beans preserve the volatile aromatics that make our roasts unique.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default App;